import Loader from '@justgiving/loading/es/Loading';
import { SelectItem } from '@justgiving/select';
import { captureException } from '@sentry/browser';
import cn from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FitnessApplicationActivityType,
  MeasurementSystem,
  PageType,
} from '../../../../types/__generated__';
import useCreateTimelineEntry from '../../../hooks/timeline/useCreateTimelineEntry';
import { useApp } from '../../../hooks/useApp';
import Button from '../../Button/Button';
import FormField from '../../FormElements/FormField/FormikFormField';
import FormSelect from '../../FormElements/FormSelect/FormikFormSelect';
import FormTextArea from '../../FormElements/FormTextArea/FormikFormTextArea';
import IsLargeScreen from '../../IsLargeScreen/IsLargeScreen';
import IsSmallOrMediumScreen from '../../IsSmallOrMediumScreen/IsSmallOrMediumScreen';
import { toMeters, UnitOfMeasure } from './Helpers//ConversionHelpers';
import {
  assertValidDuration,
  formatDuration,
  parseDuration,
  validateDuration,
} from './Helpers/DurationHelpers';
import styles from './ManualFitnessEntryModal.scss';

const getActivityTypes = (
  t: TFunction,
  subscribedActivities: FitnessApplicationActivityType[],
): Array<SelectItem & {
  value: FitnessApplicationActivityType;
}> =>
  [
    {
      isSelected: false,
      label: t('manualFitness.activityType.run'),
      value: FitnessApplicationActivityType.Run,
    },
    {
      isSelected: false,
      label: t('manualFitness.activityType.ride'),
      value: FitnessApplicationActivityType.Ride,
    },
    {
      isSelected: false,
      label: t('manualFitness.activityType.walk'),
      value: FitnessApplicationActivityType.Walk,
    },
    {
      isSelected: false,
      label: t('manualFitness.activityType.swim'),
      value: FitnessApplicationActivityType.Swim,
    },
  ].filter(activity =>
    subscribedActivities.some(
      subscribedActivity => subscribedActivity === activity.value,
    ),
  );

const getUnitOfMeasures = (
  t: TFunction,
): Array<SelectItem & {
  value: UnitOfMeasure;
}> => [
  {
    isSelected: false,
    label: t('manualFitness.unitOfMeasure.kilometers'),
    value: UnitOfMeasure.Kilometers,
  },
  {
    isSelected: false,
    label: t('manualFitness.unitOfMeasure.miles'),
    value: UnitOfMeasure.Miles,
  },
  {
    isSelected: false,
    label: t('manualFitness.unitOfMeasure.meters'),
    value: UnitOfMeasure.Meters,
  },
];

const formatDateTime = (d: Date) => {
  const numberToPaddedString = (number: number, padLength: number = 2) =>
    number.toString(10).padStart(padLength, '0');

  const year = numberToPaddedString(d.getFullYear(), 4);
  const month = numberToPaddedString(d.getMonth() + 1);
  const date = numberToPaddedString(d.getDate());
  const hours = numberToPaddedString(d.getHours());
  const minutes = numberToPaddedString(d.getMinutes());

  return `${year}-${month}-${date}T${hours}:${minutes}`;
};

interface ManualFitnessEntryFormValues {
  title: string;
  activityType: ReturnType<typeof getActivityTypes>[0]['value'];
  unitOfMeasure: ReturnType<typeof getUnitOfMeasures>[0]['value'];
  distance: number;
  duration: string;
  when: string;
  notes?: string;
}

interface ManualFitnessEntryModalProps {
  pageType: PageType;
  pageId: string;
  pageSlug?: string;
  measurementSystem?: MeasurementSystem;
  subscribedActivities: FitnessApplicationActivityType[];
  onSubmit: () => void;
  onCancel: () => void;
}

const ManualFitnessEntryModal = React.forwardRef(
  (
    {
      pageId,
      pageType,
      pageSlug,
      measurementSystem,
      subscribedActivities,
      onSubmit,
      onCancel,
    }: ManualFitnessEntryModalProps,
    ref?: React.Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation('common', { useSuspense: false });
    const { showToast } = useApp();
    const [createTimelineEntry] = useCreateTimelineEntry(measurementSystem);

    const activityTypes = useMemo(
      () => getActivityTypes(t, subscribedActivities),
      [t, subscribedActivities],
    );
    const unitOfMeasures = useMemo(() => getUnitOfMeasures(t), [t]);

    const initalValues: ManualFitnessEntryFormValues = {
      title: '',
      activityType: '' as any,
      unitOfMeasure: '' as any,
      distance: '' as any,
      duration: '',
      when: formatDateTime(new Date(Date.now())),
      notes: '',
    };

    const handleValidate = (values: ManualFitnessEntryFormValues) => {
      const errors: Partial<Record<
        keyof ManualFitnessEntryFormValues,
        string
      >> = {};

      if (!values.title) {
        errors.title = t('manualFitness.title.validation.required');
      } else if (values.title.trim() === '') {
        errors.title = t('manualFitness.title.validation.invalid');
      }

      if (!values.activityType) {
        errors.activityType = t(
          'manualFitness.activityType.validation.required',
        );
      } else if (
        !activityTypes.map(x => x.value).includes(values.activityType)
      ) {
        errors.activityType = t(
          'manualFitness.activityType.validation.invalid',
        );
      }

      if (!values.unitOfMeasure) {
        errors.unitOfMeasure = t(
          'manualFitness.unitOfMeasure.validation.required',
        );
      } else if (
        !unitOfMeasures.map(x => x.value).includes(values.unitOfMeasure)
      ) {
        errors.unitOfMeasure = t(
          'manualFitness.unitOfMeasure.validation.invalid',
        );
      }

      if (!values.distance && values.distance !== 0) {
        errors.distance = t('manualFitness.distance.validation.required');
      } else if (values.distance! <= 0) {
        errors.distance = t('manualFitness.distance.validation.invalid');
      }

      if (!values.duration) {
        errors.duration = t('manualFitness.duration.validation.required');
      } else if (!validateDuration(values.duration)) {
        errors.duration = t('manualFitness.duration.validation.invalid');
      }

      if (!values.when) {
        errors.when = t('manualFitness.when.validation.invalid');
      }

      return errors;
    };

    const handleSubmit = async (
      values: ManualFitnessEntryFormValues,
      { setSubmitting }: FormikHelpers<ManualFitnessEntryFormValues>,
    ) => {
      setSubmitting(true);

      try {
        const durationResult = parseDuration(values.duration);
        assertValidDuration(durationResult);

        await createTimelineEntry({
          type: pageType,
          pageId: pageId,
          pageSlug: pageSlug,
          message: values.notes,
          createdDate: new Date(values.when).toJSON(),
          fitness: {
            title: values.title!,
            activityType: values.activityType!,
            distance: Math.round(
              toMeters(values.unitOfMeasure!, values.distance!),
            ),
            duration: durationResult.seconds,
            elevation: 0,
          },
          media: [],
        });

        showToast({
          message: t('manualFitness.submitSuccess'),
          type: 'success',
        });
      } catch (error) {
        showToast({
          message: t('manualFitness.submitFail'),
          type: 'warning',
        });
        captureException(error);
      }

      setSubmitting(false);
      onSubmit();
    };

    return (
      <div className={styles.manualFitnessEntryModal} ref={ref}>
        <IsSmallOrMediumScreen>
          <h1>{t('manualFitness.modalTitle')}</h1>
        </IsSmallOrMediumScreen>
        <IsLargeScreen>
          <h3>{t('manualFitness.modalTitle')}</h3>
        </IsLargeScreen>

        <Formik
          onSubmit={handleSubmit}
          validate={handleValidate}
          initialValues={initalValues}
        >
          {({ isValid, isSubmitting, values }) => (
            <Form>
              <FormField
                name="title"
                label={t('manualFitness.title.label')}
                placeholder={t('manualFitness.title.placeholder')}
              />

              <FormSelect
                name="activityType"
                label={t('manualFitness.activityType.label')}
                placeholder={t('manualFitness.activityType.placeholder')}
                items={activityTypes}
              />

              <FormSelect
                name="unitOfMeasure"
                label={t('manualFitness.unitOfMeasure.label')}
                placeholder={t('manualFitness.unitOfMeasure.placeholder')}
                items={unitOfMeasures}
              />

              <FormField
                type="number"
                name="distance"
                label={t('manualFitness.distance.label')}
                placeholder={`0.00 ${unitOfMeasures.find(
                  n => n.value === values.unitOfMeasure,
                )?.label ?? unitOfMeasures[0].label}`}
              />

              <FormField
                name="duration"
                label={t('manualFitness.duration.label')}
                placeholder={t('manualFitness.duration.placeholder')}
                format={formatDuration}
              />

              <FormField
                type="datetime-local"
                name="when"
                label={t('manualFitness.when.label')}
              />

              <FormTextArea
                name="notes"
                placeholder={t('manualFitness.notes.placeholder')}
                maxLength={550}
                showCharacterCount={true}
                className={styles.notesTextArea}
              />

              <Button
                disabled={isSubmitting || !isValid}
                type="submit"
                className="jg-space-mbsm"
              >
                {isSubmitting ? (
                  <Loader size="small" colour="white" />
                ) : (
                  t('manualFitness.formSubmit')
                )}
              </Button>
              <Button
                type="button"
                onClick={onCancel}
                className={cn('jg-btn--ghost', 'jg-space-mbsm')}
              >
                {t('manualFitness.formCancel')}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    );
  },
);

export default ManualFitnessEntryModal;
